import React from 'react';
import { Trash2 } from 'lucide-react';
import { Text, Chip } from '@cotera/client/app/components/ui';

export type ToolConnectionStatus = 'connected' | 'not-connected';

export const ToolConnectionStatusBadge: React.FC<{
  label: string;
  status?: ToolConnectionStatus;
}> = ({ label, status = 'connected' }) => (
  <Chip
    appearance="inline"
    variant={status === 'connected' ? 'success' : 'neutral'}
    icon={
      status === 'connected' ? undefined : (
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
      )
    }
  >
    {label}
  </Chip>
);

export const ToolConnectionStatusCard: React.FC<{
  title: string;
  description: string;
  icon?: React.ReactNode;
  status: ToolConnectionStatus;
  statusLabel?: string;
  action?: React.ReactNode;
  isActive?: boolean;
  onDelete?: () => void;
}> = ({
  title,
  description,
  icon,
  status,
  statusLabel,
  action,
  isActive = false,
  onDelete,
}) => {
  const resolvedStatusLabel =
    statusLabel ?? (status === 'connected' ? 'Connected' : 'Not connected');

  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="flex items-start gap-3">
        {icon !== undefined && (
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background">
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <Text.P className="font-medium">{title}</Text.P>
            <Text.Caption className="text-muted-foreground mt-1 block">
              {description}
            </Text.Caption>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ToolConnectionStatusBadge
              label={resolvedStatusLabel}
              status={status}
            />
            {isActive && <Chip variant="success">Active</Chip>}
            {action}
            {onDelete !== undefined && (
              <button
                type="button"
                className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                onClick={onDelete}
                aria-label="Delete credentials"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
